/**
 * The registry model: what a report model, dimension and measure ARE.
 *
 * Deliberately free of SQL *composition*, React and any database driver — the
 * compiler (`@clockwise/semantic-server`) and the builder UI
 * (`@clockwise/report-kit`) both read these shapes, so neither can drift from
 * the other. Models themselves live in the HOST APPLICATION, not here; this
 * package only says what one must look like.
 *
 * The split that runs through this file: a `*Source` type is what the server
 * holds, and it EXTENDS the client-facing type by adding the physical half —
 * expressions, joins, the SQL. The browser is handed the base type, which is
 * the same object minus those fields. There is no parallel projection to keep
 * in sync.
 *
 * See docs/reporting/requirements.md §FR-1.
 */

import { DATE_RANGE_PRESETS } from '@clockwise/shared/date-range';
import {
  BLANK_OPS,
  DEFAULT_VALUE_TYPE,
  type FilterOp,
  type FilterValueType,
  MEMBER_OPS,
  OPERATORS_BY_TYPE,
} from './filter';
import {
  type DateRangePreset,
  MAX_DECIMALS,
  REPORT_KINDS,
  type ReportKind,
  type ReportPeriodPreset,
  reportPeriodPreset,
} from './spec';

/** Where a value came from, for display and formatting. */
export type MeasureFormat = 'number' | 'percent' | 'currency' | 'hours';

/**
 * How many decimal places each unit is READ to. The one copy.
 *
 * It lives here, beside `MeasureFormat`, because BOTH renderers need it and
 * they are in different packages: the screen builds `Intl.NumberFormat` options
 * from it (`report-kit/src/format.ts`) and the export builds an Excel pattern
 * from it (`apps/api/src/core/analytics/export.ts`). `apps/api` cannot import
 * `report-kit`, so a copy in each was a copy nothing could hold together — and
 * the failure that lets through is the precise one this table exists to
 * prevent: a column reading `1,234.56` on screen and `1,234.6` in the file it
 * was exported from.
 *
 * `hours` is 2 because `timesheets.hours` is `numeric(6, 2)` — that is the
 * stored value, not a rounded one, and decimal addition of two-place values is
 * exact at two places, so sums and subtotals are exact there too. `currency` is
 * 2 because money is settled in cents. Neither `percent` nor `number` is a
 * stored quantity, so both keep the looser precision they have always had.
 */
export const FORMAT_DECIMALS: Record<MeasureFormat, number> = {
  number: 0,
  percent: 1,
  currency: 2,
  hours: 2,
};

/**
 * The decimals a measure is rendered at — the ONE resolution both surfaces run.
 *
 * Every guard is here rather than duplicated at the two call sites, because the
 * bug class is precisely the two of them disagreeing. In particular:
 *
 *  - An UNKNOWN `format` falls back to `number` rather than to whatever the
 *    host's default happens to be. A browser holding a cached bundle against a
 *    newer API is the ordinary way this is reached, and the old switch had a
 *    `default:` arm that this restores.
 *  - `null` and `undefined` are ONE case. `decimals` is optional in TypeScript
 *    but crosses the wire as JSON, where absent optionals are readily `null`,
 *    and a resolver that treated the two differently from its own cache key
 *    poisoned that cache for the life of the page.
 *  - A value that is not a whole number in range is IGNORED rather than
 *    clamped or thrown. Neither renderer can honour it, and falling back to the
 *    unit's own precision keeps both of them reading the same.
 */
export function decimalsFor(format: MeasureFormat, decimals?: number | null): number {
  const unit = FORMAT_DECIMALS[format] ?? FORMAT_DECIMALS.number;
  if (decimals === null || decimals === undefined) return unit;
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > MAX_DECIMALS) return unit;
  return decimals;
}

/**
 * What KIND of aggregate a measure is.
 *
 * Not used to build the SQL — the author writes that. It exists so the layer
 * can answer one question without parsing an expression: **is this additive?**
 * See {@link isAdditive}.
 */
export type Agg = 'sum' | 'count' | 'countDistinct' | 'min' | 'max' | 'avg' | 'ratio' | 'custom';

/**
 * One member of a closed vocabulary, authored in the model.
 *
 * `key` is what the filter compares against — the value the dimension's
 * `keySql` actually produces, not the word a reader sees — and `label` is what
 * the reader sees. The same pair a query result carries, minus the nulls: a
 * static list is written by hand, so there is no missing value to represent.
 */
export interface DimensionOption {
  key: string;
  label: string;
}

/**
 * Where a filter's option list comes from.
 *
 *  - `'distinct'` (the default) — the values this dimension ACTUALLY TAKES in
 *    the rows the caller may see, searched server-side. It is the only source
 *    that needs no second definition of anything: the members fall out of the
 *    report's own SELECT, so they cannot drift from it, and they cannot include
 *    a value the caller is not allowed to see, because the SELECT is where row
 *    visibility is already decided.
 *  - a LITERAL LIST — a short, closed vocabulary the model states outright.
 *    Worth it only where the set is genuinely fixed and known in code (a
 *    boolean, a status enum): it costs no query at all, and the values a
 *    period happens to contain do not decide what can be asked for.
 *  - `'none'` — no list. The control takes the value as typed. For a dimension
 *    whose members are neither enumerable nor worth enumerating.
 */
export type FilterOptions = 'distinct' | 'none' | readonly DimensionOption[];

/** What a dimension may be used for. Every flag defaults to true. */
export interface Capability {
  /** May it go on a Rows or Columns shelf? */
  groupable?: boolean;
  /** May an axis be sorted by it? */
  sortable?: boolean;
  /** Does it appear in the filter panel? */
  filterable?: boolean;
  /** Where the filter's option list comes from. Defaults to `'distinct'`. */
  options?: FilterOptions;
  /**
   * What KIND of thing the column holds, which decides the operators its
   * filter offers. Defaults to `'lookup'` — what every dimension was before
   * operators existed, so a model that says nothing behaves as it did.
   */
  valueType?: FilterValueType;
}

export const canGroup = (d: Dimension) => d.capability?.groupable !== false;
export const canSort = (d: Dimension) => d.capability?.sortable !== false;
export const canFilter = (d: Dimension) => d.capability?.filterable !== false;

/**
 * The type this dimension's filter speaks.
 *
 * ONE reader, for the same reason {@link filterOptions} is one: the builder
 * draws the operator menu from it and `validateSpec` refuses an operator
 * against it, and those two disagreeing is a filter you can build and not run.
 */
export const valueTypeOf = (d: Dimension): FilterValueType =>
  // A column that declares `options: 'none'` has no enumerable members, which
  // is the one thing a lookup is: a key with a label, picked from a list. So
  // the fallback for one is TEXT — the string operators are what is left to
  // ask of it, and defaulting to `lookup` left such a column offering only
  // member operators with no list behind them.
  d.capability?.valueType ?? (filterOptions(d) === 'none' ? 'text' : DEFAULT_VALUE_TYPE);

/**
 * Where this dimension's filter values come from.
 *
 * ONE reader for the default, so the client's picker and the server's endpoint
 * cannot disagree about which dimensions are searched and which are stated. A
 * dimension that says nothing is searched — the safe default, because a list
 * derived from the rows is never wrong about what the rows contain.
 */
export const filterOptions = (d: Dimension): FilterOptions => d.capability?.options ?? 'distinct';

/** The literal members, or null when this dimension's list is not authored. */
export const staticOptions = (d: Dimension): readonly DimensionOption[] | null => {
  const source = filterOptions(d);
  return Array.isArray(source) ? source : null;
};

/**
 * A column you can group or filter by, as the client sees it.
 *
 * Carries no SQL. What the client needs is the id (to name it in a spec), the
 * label (to show it) and what it may be used for.
 */
export interface Dimension {
  id: string;
  label: string;
  /** One sentence on what this splits by. Shown in the picker. */
  description?: string;
  /** True for date-like dimensions; line charts require one on the row axis. */
  isTime?: boolean;
  /** Where drilling from this dimension lands. Falls back to the model's. */
  drillTo?: DrillTarget;
  /**
   * The dimensions that, pinned TOGETHER, pin this one too.
   *
   * Tenure is a fact about an employee: name the employee and there is one
   * tenure band left to name. Manager / Lead needs two — it is the lead of the
   * customer when the employee is on that customer's team, so neither alone
   * decides it. A LIST, and every entry must be pinned.
   *
   * It does two jobs in choosing where a drill may land, and both are
   * load-bearing (see {@link drillEligibility}):
   *
   *  - A pinned attribute the landing CANNOT filter on is dropped rather than
   *    ruling the landing out — its determinant already narrows to it.
   *  - A landing axis that is an attribute of what is pinned does not count as
   *    a new breakdown. It would print one row.
   *
   * WITHIN ONE WORK DATE, not across a period. Entity attributes are read as
   * of each entry's own version, so an employee whose manager changed in July
   * has two managers in July — and the drill, which carries the whole
   * coordinate, still lists exactly the rows of the cell clicked. What this
   * field must never be is WRONG within a day: `activityRole` filed under
   * `employee` would hide every drill that splits a person by role, with
   * nothing on screen to say so. `attributes.integration.test.ts` checks the
   * data against every entry here.
   */
  attributeOf?: readonly string[];
  /** Section heading in the builder's picker ("People", "Engagement", "Time"). */
  group?: string;
  capability?: Capability;
}

/**
 * Where a drill lands.
 *
 * There is ONE drill. Clicking always does the same thing — carry the whole
 * coordinate as filters (D-20) — and only the destination varies, so going a
 * level deeper and opening another report are the same operation with a
 * different target rather than two features.
 *
 *   `{ dimension: 'srController' }`            same report, grouped one level down
 *   `{ report: 'timesheets', map: {…} }`       the rows underneath
 *   `{ report: 'orders', dimension: 'sku' }`   another report, at a chosen grain
 */
export interface DrillTarget {
  /** The report to open. Omit to stay in this one. */
  report?: string;
  /** What the landing report groups by. Omit to keep the current grouping. */
  dimension?: string;
  /**
   * A LISTING's columns, when the landing is one — the rows behind a figure.
   *
   * A dimension the click pinned to one value is left out when the listing is
   * drawn: every row would print the same word, and the tray already names it
   * among the filters carried in.
   */
  columns?: readonly string[];
  /**
   * `thisDimensionId → thatFilterName`, **only where the names differ**. A
   * dimension absent from the map passes through under its own id.
   *
   * Requiring every model to agree on names would couple them to each other and
   * break the first time one is renamed; requiring a full map for every pair
   * would be ceremony for the common case where they already agree.
   */
  map?: Record<string, string>;
}

/**
 * A dimension with its physical mapping. Server only.
 *
 * Both expressions are authored. `keySql` is what the query groups and filters
 * on; `labelSql` is what the reader sees. Three shapes fall out with no extra
 * field:
 *
 *  - columns already on the view — set both, no `join`
 *  - key on the fact, label from a lookup — set both plus `join`
 *  - a derived bucket (tenure band, POD) — set `labelSql` only; it is also the
 *    key, so the bucket filters like any other value
 */
export interface DimensionSource extends Dimension {
  /** Grouped and filtered on. Defaults to `id`. */
  keySql?: string;
  /**
   * The source whose rows carry this dimension's INTERESTING values.
   *
   * Almost no dimension needs this. It exists for the one shape where a column
   * is present on several relations and says something different on each — the
   * facts answering a constant while another SELECT does the real test — so a
   * filter on it is only meaningful when that other relation is actually read.
   *
   * Without it the filter passes {@link unreachableFilter}, which asks only
   * whether SOME relation can apply it, and the facts can. It then narrows the
   * facts to nothing and the report comes back empty, which reads as a
   * legitimate "none" rather than as a question this report could not ask.
   */
  requiresSource?: string;
  /**
   * Displayed ON A FACT ROW — a listing's cell, where the question is what the
   * entry said on the day it was logged. Defaults to `keySql`, which defaults
   * to `id`.
   *
   * THE INVARIANT: for anything that GROUPS, a label must be a function of the
   * key. It is not automatically true here, because these are authored
   * expressions over a flattened fact view and a versioned entity resolves its
   * name AS OF the work date — so one customer renamed mid-period has one key
   * and two labels. The compiler used to group by the label as well as the key,
   * which turned that into two result rows for one group, and the projection
   * kept one of them. Hours disappeared from the grid while the server's own
   * totals still counted them.
   *
   * So a dimension whose `labelSql` can vary within a key MUST declare
   * {@link DimensionSource.groupLabelSql}. Enforced by `model.test.ts`, and
   * caught at runtime by the row-count check in `AnalyticsService.run`.
   */
  labelSql?: string;
  /**
   * Displayed FOR A GROUP — a pivot's row or column header, and the filter
   * picker. Defaults to `labelSql`.
   *
   * A group header names an IDENTITY, not a fact: it says *which* customer,
   * not what was true of it in August. The product already draws that line for
   * dropdowns — a picker selects an identity, which is the one thing versioning
   * never changes, so only the predicate moves to the version (#619). This is
   * the same rule applied to the row axis.
   *
   * In warehouse terms `labelSql` is the type-2 attribute and this is the
   * type-1 one. Only the two versioned entities need it; every other dimension
   * here already reads a live dimension table or derives its label from its own
   * key, so both are functions of the key and this field stays absent.
   *
   * WHATEVER SUPPLIES THIS MUST EXIST ON EVERY SOURCE THAT ANSWERS AT THIS
   * DIMENSION'S GRAIN — the aggregate emits it from each side, and a source
   * missing it is dropped as unanswerable, which turns its measures into em
   * dashes with nothing on screen to say why.
   */
  groupLabelSql?: string;
  /** Which {@link JoinDef} supplies `labelSql`, when it needs one. */
  join?: string;
}

export const keyExpr = (d: DimensionSource) => d.keySql ?? d.id;
export const labelExpr = (d: DimensionSource) => d.labelSql ?? keyExpr(d);
/** The label an AGGREGATE shows. See {@link DimensionSource.groupLabelSql}. */
export const groupLabelExpr = (d: DimensionSource) => d.groupLabelSql ?? labelExpr(d);

/**
 * A number, with the formula that produced it.
 *
 * `formulaText` is not documentation — it is returned in the query response and
 * rendered in the widget footer, so the definition a user reads is guaranteed
 * to be the definition that ran.
 */
export interface Measure {
  id: string;
  label: string;
  /**
   * One sentence on what this measures and when to reach for it — the thing a
   * finance user needs that a label cannot carry. Rendered beside the formula
   * in the picker, so choosing a measure never requires asking a colleague.
   */
  description?: string;
  agg: Agg;
  /** Plain-English formula. Shipped to the UI with every result. */
  formulaText: string;
  /**
   * The formula in the model's OWN vocabulary — other measures' LABELS joined
   * by operators, never SQL.
   *
   * `formulaText` says what the number means in a sentence; this says what it
   * is made of, in terms the reader can look up in the same picker. "Client
   * billable + client non-billable hours" is prose that happens to read like a
   * formula and is free to drift from the expression; `Client Billable Hours +
   * Client Non-Billable Hours` is generated FROM the expression, so it cannot.
   *
   * Written by `toCatalog` from {@link formulaFor} and by nothing else. Absent
   * for a measure that is not composed of others — repeating a leaf's own label
   * back at the reader is noise, not a definition.
   */
  formula?: string;
  format: MeasureFormat;
  /**
   * Decimal places this measure is read to, WHEN IT DISAGREES WITH ITS UNIT.
   *
   * The unit is the default and {@link FORMAT_DECIMALS} states it: hours to the
   * hundredth because that is how they are stored, money to the cent. This
   * field is the escape hatch for a measure whose quantity genuinely differs —
   * an effective rate wanted to four places, say — and NOTHING DECLARES ONE
   * TODAY. `model.test.ts` asserts that, so the first entry is a deliberate
   * claim, not a drift.
   *
   * Read it as an override, not as where precision is configured. Setting it to
   * the value its unit already has is a no-op; if a whole unit is being read at
   * the wrong precision, `FORMAT_DECIMALS` is the line to change, and changing
   * it moves every surface at once — screen, chart, tile and both exports.
   *
   * Resolved through {@link decimalsFor}, which is also what makes an
   * out-of-range value harmless: a whole number in 0–20 or the unit's own
   * precision, never a thrown `RangeError` on one surface and a malformed
   * spreadsheet on the other.
   *
   * A declared precision PADS as well as truncates — `decimals: 4` renders 8 as
   * "8.0000" — because a column whose cells disagree about their decimal places
   * is a column that has to be read twice.
   */
  decimals?: number;
  /**
   * The MEASURE this one's numerator is, for a ratio — the same reference by
   * name that {@link Measure.denominator} already is, and for the same reason.
   * `Overall Utilization %` divides utilized hours by available hours, and both
   * of those are quantities this model declares; repeating either one's SQL
   * here is a second copy free to drift from the first.
   *
   * Falls back to {@link MeasureSource.sql} when absent, so a numerator that is
   * genuinely not one of the model's own measures can still be written out —
   * and such a ratio has no total anywhere, because there is no named quantity
   * to rebuild one from. See {@link ratioParts}.
   */
  numerator?: string;
  /**
   * The MEASURE this one divides by. Required iff `agg: 'ratio'`; the numerator
   * is {@link Measure.numerator}, or `sql` when that is absent.
   *
   * A measure id, not an expression. The denominator of a ratio is a quantity
   * the model already declares — available hours, budgeted hours, quota — so
   * repeating its SQL here would be two copies of one definition, free to
   * drift the moment either is edited. Naming it also makes the relationship
   * legible to code that needs it: which measures share a denominator, and
   * therefore which rows a filter has to spare.
   */
  denominator?: string;
  /** Required only for `agg: 'custom'`; ignored otherwise. */
  additive?: boolean;
  group?: string;
  /**
   * May this measure be a COLUMN of a detail listing? See {@link canList}.
   *
   * Written by `toCatalog` from {@link MeasureSource.rowSql}, and by nothing
   * else — it is the same fact, projected. A model author sets `rowSql`; this
   * is what survives the trip to a browser that must never see the expression.
   */
  listable?: boolean;
}

/**
 * A measure with its expression. Server only.
 *
 * The author writes the aggregate; the compiler builds the envelope around it
 * and never reaches inside. A composed aggregate can express exactly one shape,
 * and `Billable $` — hours × rate, materialised per entry — is not that shape.
 *
 * Fan-out safety does not depend on the expression: {@link JoinDef} permits
 * only verified many-to-one joins, which cannot multiply fact rows whatever the
 * measure sums.
 */
export interface MeasureSource extends Measure {
  /**
   * The aggregate expression. Defaults to the sum of {@link MeasureSource.parts}
   * when those are named, and to `AGG(id)` when neither is.
   */
  sql?: string;
  /**
   * The PER-ROW expression, for a detail listing. `hours` where `sql` is
   * `SUM(hours)`.
   *
   * Authored rather than derived, because you cannot get one from the other by
   * stripping text: `COUNT(DISTINCT x)` has no row-level form at all, and
   * neither does a ratio. Absent, the measure simply cannot be a listing
   * column — which is the right answer for `overallUtilPct`.
   */
  rowSql?: string;
  /**
   * The MEASURES this one is the sum of, in place of writing the expression.
   *
   * `Total Client Hours` is billable plus non-billable, and saying so with
   * `parts: ['billableHours', 'nonBillableHours']` gives ONE definition where
   * `sql: 'SUM(billable_hours + nonbillable_hours)'` gave three copies of it —
   * the aggregate, the row-level form, and the sentence in `formulaText` — free
   * to drift the moment a column is renamed in one of them. Both expressions
   * are then composed from the parts' own, so the emitted SQL is exactly what
   * writing it by hand produced, and {@link formulaFor} can name the parts back
   * to the reader because it is reading the definition rather than a paraphrase
   * of it.
   *
   * Ignored wherever the expression is authored outright — `sql` for the
   * aggregate, `rowSql` for the row-level form. An expression that is not a
   * plain sum of other measures says so by writing itself out, and the written
   * one is the one that runs.
   */
  parts?: readonly string[];
  /**
   * Which of the model's {@link ReportModelSource.sources} this reads. Omit
   * for the model's own facts SELECT.
   *
   * Available hours are not a fact anyone records — they are working days
   * times eight, for one person, over one period. Selecting them from their
   * own SELECT rather than from rows unioned into the facts is what makes an
   * entry-level filter unable to touch them: there is no customer column
   * there to match against, so nothing has to be exempted.
   */
  source?: string;
  /**
   * `formulaText` already spells the arithmetic out, in the underlying fields.
   *
   * Suppresses {@link formulaFor}'s line for this measure. That line names the
   * measures a number is built from, which reads well only when those measures
   * are on screen beside it — and a report shows whichever columns someone
   * picked. `(Over)/Under ÷ Budget Hours` under a lone percentage column cites
   * two definitions the reader cannot see, and under a `formulaText` that
   * already states the division it is the same formula printed twice.
   */
  formulaStated?: true;
}

const AGG_FN: Record<Agg, string> = {
  sum: 'SUM',
  count: 'COUNT',
  countDistinct: 'COUNT',
  min: 'MIN',
  max: 'MAX',
  avg: 'AVG',
  ratio: 'SUM',
  custom: 'SUM',
};

/** Just enough of a model to resolve one measure's references to another. */
type MeasureModel = { measures: readonly Measure[] };

/**
 * One measure another one names, or an error naming both ends of the break.
 *
 * A dangling reference is a model bug that would otherwise surface as a missing
 * column or a silently wrong expression, so it is refused where it is read.
 */
function referenced(model: MeasureModel, from: MeasureSource, id: string): MeasureSource {
  const found = findMeasure(model, id) as MeasureSource | undefined;
  if (!found) {
    throw new Error(`Measure "${from.id}" names "${id}", which this model does not declare.`);
  }
  return found;
}

/**
 * A composed measure flattened to the measures it is ULTIMATELY made of.
 *
 * Flattened rather than kept as a tree because addition is associative, so
 * `(billable + non-billable) + msCFO` and `billable + non-billable + msCFO` are
 * the same quantity — and the flat form is both the expression a hand-written
 * `sql` would have had and the formula a reader wants to see. A measure that
 * names no parts is its own only leaf, which is what lets every caller here
 * treat composed and plain measures alike.
 */
function leaves(
  model: MeasureModel,
  m: MeasureSource,
  seen: readonly string[] = [],
): MeasureSource[] {
  if (seen.includes(m.id)) {
    // Left alone this recurses until the stack ends, and it would do so inside
    // query compilation — an infinite loop serving a request rather than a
    // model that fails to load.
    throw new Error(`Measure "${m.id}" is composed, through ${seen.join(' → ')}, of itself.`);
  }
  if (!m.parts?.length) return [m];
  const next = [...seen, m.id];
  return m.parts.flatMap((id) => leaves(model, referenced(model, m, id), next));
}

/** The row-level expressions of a composed measure's leaves, summed. */
function leafRowSql(model: MeasureModel, m: MeasureSource): string {
  return leaves(model, m)
    .map((leaf) => {
      if (typeof leaf.rowSql !== 'string') {
        throw new Error(
          `Measure "${m.id}" is composed of "${leaf.id}", which has no row-level form to add.`,
        );
      }
      return leaf.rowSql;
    })
    .join(' + ');
}

/**
 * The measure's expression: authored, composed from its parts, or `AGG(id)`.
 *
 * Takes the model because a measure may now name others — a ratio its
 * numerator, a sum its parts — and resolving a name needs the list it is a
 * name in. The same reason {@link denominatorExpr} has always taken one.
 */
export function measureExpr(model: MeasureModel, m: MeasureSource): string {
  if (m.agg === 'ratio') {
    if (!m.denominator) {
      // A ratio's expression is only its NUMERATOR — the caller composes the
      // division. Without a denominator that caller emits a bare aggregate, and
      // the column reports a sum where a quotient was declared. There is no
      // model loader yet to assert this at boot, so it is asserted here, at the
      // one point every SQL-composing path goes through.
      throw new Error(`Measure "${m.id}" declares agg: 'ratio' but names no denominator.`);
    }
    if (m.numerator) {
      const num = referenced(model, m, m.numerator);
      if (num.agg === 'ratio') {
        // Same reasoning as the denominator's own guard below: a ratio of
        // ratios is not a quantity, and following the reference has no bottom.
        throw new Error(`Measure "${m.id}" divides "${num.id}", which is itself a ratio.`);
      }
      return measureExpr(model, num);
    }
  }
  if (m.sql) return m.sql;
  if (m.parts?.length) return `${AGG_FN[m.agg]}(${leafRowSql(model, m)})`;
  const inner = m.agg === 'countDistinct' ? `DISTINCT ${m.id}` : m.id;
  return `${AGG_FN[m.agg]}(${inner})`;
}

/**
 * The formula in the model's own vocabulary, or `undefined` when there is
 * nothing to say. See {@link Measure.formula}.
 *
 * Generated from the SAME declarations the SQL is generated from, which is the
 * whole point: a reader who opens the definitions strip is looking at the
 * composition that ran, not at a sentence somebody remembered to update.
 *
 * Only COMPOSED measures get one. `Client Billable Hours = Client Billable
 * Hours` is a tautology, and a strip full of them is one a reader stops
 * opening; the plain-English `formulaText` is what such a measure has to say.
 */
export function formulaFor(model: MeasureModel, m: MeasureSource): string | undefined {
  if (m.formulaStated) return undefined;
  if (m.agg === 'ratio') {
    // A ratio is a QUOTIENT, and a formula that states only its numerator
    // describes a different quantity — a percentage published as a sum of
    // hours. So the division is stated whichever way the numerator was
    // declared, by reference or as this measure's own parts, and a ratio whose
    // halves cannot both be named says nothing rather than half of itself.
    const den = m.denominator ? findMeasure(model, m.denominator) : undefined;
    const num = m.numerator
      ? (findMeasure(model, m.numerator) as MeasureSource | undefined)
      : m.parts?.length
        ? m
        : undefined;
    if (!num || !den) return undefined;
    const top = sumOfLabels(model, num);
    // Parenthesised only when the numerator is itself a sum, because
    // `A + B ÷ C` reads as `A + (B ÷ C)` — the wrong quantity, stated
    // confidently.
    return `${top.includes(' + ') ? `(${top})` : top} ÷ ${den.label}`;
  }
  if (m.parts?.length) return sumOfLabels(model, m);
  return undefined;
}

/** A measure's leaves, named by their labels. */
function sumOfLabels(model: MeasureModel, m: MeasureSource): string {
  return leaves(model, m)
    .map((leaf) => leaf.label)
    .join(' + ');
}

/**
 * May this measure be a COLUMN of a detail listing?
 *
 * One implementation for both ends, reading whichever half of the fact it is
 * given: `rowSql` on the server, `listable` on the client. `toCatalog` is the
 * only thing that ever writes the second, and it writes it from this function,
 * so there is nothing to keep in step.
 *
 * A measure that reads a SOURCE is never listable whatever it declares. Its
 * rows are not the facts being listed — available hours belong to a person and
 * a period, not to a timesheet entry — so there is no row to put the value on.
 */
export function canList(model: MeasureModel, m: Measure): boolean {
  const source = m as MeasureSource;
  if (source.source) return false;
  // An AUTHORED row form first, exactly as `measureExpr` takes an authored
  // `sql` over the parts. Reading the parts first meant that adding them to a
  // measure that already had `rowSql` — for the definitions strip, changing no
  // SQL — could take the column out of the picker, or list a quantity the
  // pivot above it never totalled.
  if (typeof source.rowSql === 'string') return true;
  // Otherwise a composed measure is listable exactly when everything it is made
  // of is: its row-level form is its leaves' added together, so one leaf
  // without one leaves the sum with nothing to put in the column.
  if (source.parts?.length) {
    return leaves(model, source).every((leaf) => !leaf.source && typeof leaf.rowSql === 'string');
  }
  return m.listable === true;
}

/**
 * May this FIELD be filtered — a dimension or a measure?
 *
 * A filter applies to a SOURCE ROW, always. That is what every filter written
 * before measures could be filtered already did, and it is the only reading
 * under which "Over Budget Hours < 10" and "Customer is Acme" mean the same
 * KIND of thing: keep the rows where this is true, then aggregate what is
 * left. Testing a measure against its group TOTAL instead would put two
 * different questions under one word, and the reader would have no way to tell
 * from the chip which one they had asked.
 *
 * So a measure can be filtered exactly when it has a per-row value —
 * {@link canList}, the same rule that decides whether it can be a column of a
 * detail listing, because it is the same question. A ratio and a
 * `COUNT(DISTINCT …)` have no row-level form at all: there is nothing on one
 * row to compare, and offering the filter would mean silently switching to the
 * other meaning.
 */
export function canFilterField(model: ReportModel & MeasureModel, field: string): boolean {
  const dimension = findDimension(model, field);
  if (dimension) return canFilter(dimension);
  const measure = findMeasure(model, field);
  return measure ? canList(model, measure) : false;
}

/**
 * The type a filter on this field speaks, or `null` when it cannot be filtered.
 *
 * A measure is a NUMBER — every one of them is, `format` decides how it is
 * printed rather than what it is — so its operators are the ordered ones
 * without anything being declared per measure.
 */
export function filterValueType(
  model: ReportModel & MeasureModel,
  field: string,
): FilterValueType | null {
  const dimension = findDimension(model, field);
  if (dimension) return canFilter(dimension) ? valueTypeOf(dimension) : null;
  const measure = findMeasure(model, field);
  if (!measure) return null;
  return canList(model, measure) ? 'number' : null;
}

/**
 * The operators this field offers, in menu order.
 *
 * A dimension's come from its type. A MEASURE's are {@link MEASURE_OPS} — the
 * ordered comparisons and the blank checks, and deliberately not `is any of`:
 * that operator exists on the numeric types to keep old saved filters valid,
 * and no measure has any. Offering it produced a chip with an operator the
 * editor could not fill.
 */
export function operatorsFor(
  model: ReportModel & MeasureModel,
  field: string,
): readonly FilterOp[] {
  const dimension = findDimension(model, field);
  if (dimension) {
    if (!canFilter(dimension)) return [];
    const offered = OPERATORS_BY_TYPE[valueTypeOf(dimension)];
    // YOU CANNOT PICK FROM A LIST THAT DOES NOT EXIST. `is any of` belongs to
    // a column whose members are a set somebody can be shown — a status, a
    // department, a customer. QB ID, Keka ID and Timesheet Description have
    // one value per row and declare `options: 'none'`, so offering it made a
    // free-text column read like a lookup and handed the reader an operator
    // with no picker behind it. What is left is what a string is actually
    // asked: is, is not, contains, starts with, ends with, blank.
    return filterOptions(dimension) === 'none'
      ? offered.filter((op) => !(MEMBER_OPS as readonly FilterOp[]).includes(op))
      : offered;
  }
  const measure = findMeasure(model, field);
  // A measure IS a number, so it offers a number's operators — one list, not
  // a second one that could drift from it.
  return measure && canList(model, measure) ? OPERATORS_BY_TYPE.number : [];
}

/**
 * The operators a SPEC may carry on this field — what {@link operatorsFor}
 * offers, plus the blank checks on any dimension (the grammar; `allowsOp` is the menu). Validation
 * reads this; the menus read `operatorsFor`.
 */
export function acceptedOperatorsFor(
  model: ReportModel & MeasureModel,
  field: string,
): readonly FilterOp[] {
  const offered = operatorsFor(model, field);
  const dimension = findDimension(model, field);
  if (!dimension || !canFilter(dimension)) return offered;
  return [...new Set([...offered, ...BLANK_OPS])];
}

/** The label a filter on this field prints, whichever kind it is. */
export const fieldLabel = (model: ReportModel & MeasureModel, field: string): string =>
  findDimension(model, field)?.label ?? findMeasure(model, field)?.label ?? field;

/** The per-row expression, or `undefined` if this measure cannot be listed. */
export function rowExpr(model: MeasureModel, m: MeasureSource): string | undefined {
  if (!canList(model, m)) return undefined;
  if (typeof m.rowSql === 'string') return m.rowSql;
  return m.parts?.length ? leafRowSql(model, m) : undefined;
}

/**
 * Which way a field should sort the FIRST time it is asked for.
 *
 * Biggest-first for a NUMBER, because "who logged the most hours" is the
 * question a measure is clicked to answer; A→Z for everything else, because a
 * name, a date or a status read forwards.
 *
 * Resolved against the model rather than by testing for the literal `'label'`.
 * That shortcut held only while the sortable fields were `'label'` plus
 * measures — which stopped being true when a tabular report began sorting by
 * its own columns, and made every name and date column open backwards.
 */
export function defaultSortDir(
  model: { measures: readonly Measure[] },
  by: string,
): 'asc' | 'desc' {
  return findMeasure(model, by) ? 'desc' : 'asc';
}

/**
 * The expression a ratio divides by, resolved from the measure it names.
 *
 * Separate from {@link measureExpr} because resolving a reference needs the
 * model, and because the numerator and the denominator are two questions: the
 * caller composes the division (with its own `NULLIF`) rather than being
 * handed a quotient it cannot take apart.
 */
export function denominatorExpr(
  model: { measures: readonly MeasureSource[] },
  m: MeasureSource,
): string {
  if (!m.denominator) {
    throw new Error(`Measure "${m.id}" declares agg: 'ratio' but names no denominator.`);
  }
  const d = findMeasure(model, m.denominator);
  if (!d) {
    throw new Error(
      `Measure "${m.id}" divides by "${m.denominator}", which this model does not declare.`,
    );
  }
  if (d.agg === 'ratio') {
    // A ratio of a ratio is not a quantity, and the nesting has no bottom.
    throw new Error(`Measure "${m.id}" divides by "${d.id}", which is itself a ratio.`);
  }
  return measureExpr(model, d);
}

/**
 * Whether a measure's values may be added together.
 *
 * The one semantic property the layer derives, and it decides four things: may
 * a total be printed, may `showAs` be offered, may a running total be offered,
 * and may rows be ranked by this measure across a column axis. Averages,
 * extrema, distinct counts and ratios are all non-additive — summing them
 * produces a confident wrong number.
 */
export function isAdditive(m: Measure): boolean {
  if (m.agg === 'custom') return m.additive === true;
  return m.agg === 'sum' || m.agg === 'count';
}

/**
 * A declared many-to-one join to a lookup table.
 *
 * Only N:1 is expressible, because only N:1 cannot fan out and corrupt an
 * aggregate. The server verifies at boot that the target really is unique on
 * these columns; a registry entry that merely CLAIMS `many_to_one` is not
 * trusted.
 */
export interface JoinDef {
  id: string;
  /** `'fact'` or another join id, for chains (depth 3 max). */
  from: string;
  table: string;
  alias: string;
  /** `[leftColumn, rightColumn]` pairs. MUST include the tenant column. */
  on: ReadonlyArray<readonly [string, string]>;
  /** The only permitted relationship. Present so the intent is explicit. */
  relationship: 'many_to_one';
  /**
   * Set only when the target's uniqueness cannot be machine-verified (a plain
   * view, say). Makes the exception visible in review rather than implicit.
   */
  assumeUnique?: boolean;
}

/** One queryable subject area, as the client sees it. */
export interface ReportModel {
  id: string;
  label: string;
  /**
   * What this subject area answers, in one sentence, for somebody choosing
   * between it and the next one. Two models sharing no vocabulary is the whole
   * reason the choice is first — and a bare name ("Utilization", "Timesheets")
   * does not say which of them holds the question being asked.
   */
  description?: string;
  /**
   * Which KINDS of report this subject area may be built as. Omit for both.
   *
   * A property of the MODEL rather than of the builder, because whether a
   * question has rows underneath it is a fact about the subject area and not
   * about the screen it was asked from. Utilization is the case that forced
   * it: its numbers are a person's capacity over a period, so a listing of
   * them has no row to be one OF — the entries live in Timesheets, which is
   * where a utilization cell already drills to.
   *
   * Declared as the ALLOWED list rather than as a `noDetail` flag, so a model
   * that is only ever a listing says so in the same field, and so the builder
   * renders the choice FROM the model instead of from a rule about it.
   *
   * Enforced in `validateSpec` — the compiler's own gate — so a spec naming a
   * refused kind is a 400 wherever it came from, rather than merely a button
   * the builder declined to draw.
   */
  kinds?: readonly ReportKind[];
  /**
   * Which periods the period control OFFERS for this subject area. Omit for
   * every one a report may be asked over.
   *
   * Budget vs Actuals is the case that forced it: a budget is funded per
   * whole period, so a hand-picked window that cuts one in half compares half
   * a period's actuals against all of its budget. Declared as the ALLOWED
   * list, like `kinds`, so a preset added later does not appear on a model
   * that opted out of the ones it knew about.
   *
   * Presentation, NOT a gate: `validateSpec` does not read it. The app itself
   * asks this model over arbitrary windows — the customer Budgets tab and the
   * approval over-budget signal both run month-aligned spans that are not a
   * macro — and refusing `custom` at the compiler would refuse those.
   */
  periods?: readonly ReportPeriodPreset[];
  dimensions: readonly Dimension[];
  measures: readonly Measure[];
  /**
   * Where a drill lands when the thing clicked names no target of its own —
   * a data cell rather than a dimension's header. Omit and cells do not drill.
   */
  drillTo?: DrillTarget;
}

/**
 * The kinds this model may be built as, in the order they should be offered.
 *
 * Both when the model says nothing — the answer for every model with no reason
 * to care — and when it says `[]`, because a model that can be built as
 * NOTHING is one nobody can report on at all; reading an empty list as "no
 * restriction" fails towards a working builder rather than a blank one.
 */
export function modelKinds(
  model: Pick<ReportModel, 'kinds'>,
): readonly [ReportKind, ...ReportKind[]] {
  // Typed as non-empty so callers that need ONE kind — the kind a fresh report
  // opens as, the kind a refused one is coerced to — can take the first without
  // a fallback that could never run.
  const declared = model.kinds;
  return declared && declared.length > 0
    ? (declared as readonly [ReportKind, ...ReportKind[]])
    : REPORT_KINDS;
}

/** May this model be built as this kind of report? */
export function allowsKind(model: Pick<ReportModel, 'kinds'>, kind: ReportKind): boolean {
  return modelKinds(model).includes(kind);
}

/**
 * The periods this model's period control offers, in the standard order.
 *
 * Every report period when the model says nothing — and when it says `[]`,
 * for the reason {@link modelKinds} gives: a control offering no period at all
 * is a report nobody can run. Filtered through `DATE_RANGE_PRESETS` rather
 * than returned as declared, so the order on screen is the one every other
 * period control uses.
 */
export function modelPeriods(model: Pick<ReportModel, 'periods'>): readonly ReportPeriodPreset[] {
  const declared = model.periods;
  return declared && declared.length > 0
    ? DATE_RANGE_PRESETS.filter((p) => declared.includes(p))
    : DATE_RANGE_PRESETS;
}

/**
 * Does this model's period control offer this period?
 *
 * Read off the declaration directly rather than through {@link modelPeriods},
 * which allocates: this runs once per preset per tile in `sharedPeriods`.
 */
export function allowsPeriod(
  model: Pick<ReportModel, 'periods'>,
  preset: ReportPeriodPreset,
): boolean {
  const declared = model.periods;
  return !declared || declared.length === 0 || declared.includes(preset);
}

/**
 * Why this model cannot be asked over this period, in a sentence — or null.
 *
 * ONE sentence for every place that refuses one: the period chip shows it and
 * keeps the panel open, the spec reducer withholds the change, and the builder
 * explains a saved report it had to move. Takes any {@link DateRangePreset}
 * because that is what a control hands over; one a report cannot store reads
 * as `custom`, the same mapping `reportPeriodPreset` makes when storing it.
 */
export function periodRefusal(
  model: Pick<ReportModel, 'periods' | 'label'>,
  preset: DateRangePreset,
): string | null {
  const stored = reportPeriodPreset(preset);
  if (allowsPeriod(model, stored)) return null;
  return stored === 'custom'
    ? `${model.label} is reported over whole periods, not a custom range.`
    : `${model.label} does not offer this period.`;
}

/**
 * The periods a control shared by several models may offer: the ones EVERY
 * model offers.
 *
 * A dashboard's period drives all of its tiles, so offering a period one tile
 * refuses would put that tile on a window its own builder would never have
 * let it be asked over. No models — an empty dashboard — offers everything.
 */
export function sharedPeriods(
  models: readonly Pick<ReportModel, 'periods'>[],
): readonly ReportPeriodPreset[] {
  return DATE_RANGE_PRESETS.filter((p) => models.every((m) => allowsPeriod(m, p)));
}

/**
 * A report model with its physical half. Never leaves the server.
 *
 * `ReportModel` is not projected out of this — it IS this, minus the fields
 * below. Anything that needs only the vocabulary takes the base type.
 */
export interface ReportModelSource extends ReportModel {
  /**
   * The SELECT the facts come from — the statement itself, not the name of a
   * database object. The compiler wraps it in a CTE and groups over it.
   *
   * No view, no function, nothing to migrate. A model change and the SQL it
   * runs land in the same commit and deploy together, which is the whole
   * reason: this repo's database objects drift from its code, and a model that
   * names an object it cannot see is a model that fails at runtime instead of
   * at review.
   */
  sql: string;
  /**
   * Further SELECTs, each emitted as a sibling CTE. Keyed by the name a
   * measure names in {@link MeasureSource.source}.
   *
   * The reason any exist: a measure whose denominator is not a recorded fact.
   * Available hours are working days times eight for one person over one
   * period — arithmetic, not rows. Union that arithmetic into the facts as
   * manufactured rows and those rows get a `customer_id` that is always NULL,
   * which makes a customer filter *applicable* to them and therefore wrong: it
   * deletes the denominator. A separate SELECT with no customer column at all
   * makes the question unaskable rather than answerable-and-wrong.
   *
   * The compiler aggregates each SELECT separately and joins them on the
   * dimensions both expose. Nothing declares which those are — a SELECT either
   * has the column or it does not, so the column list IS the declaration and
   * cannot disagree with itself. Group by something only the facts have —
   * Customer, say — and there is no capacity row to join, so the ratio is NULL
   * at that cell and renders as an em dash. The grain rule falls out of the
   * join instead of needing a guard.
   *
   * **Emitted in declaration order**, so a later source may read an earlier
   * one by name. That is how an attribute two SELECTs share — who is full-time,
   * which region someone delivers from — is written once rather than copied.
   *
   * The join must be `IS NOT DISTINCT FROM`, not `=`. A derived bucket is NULL
   * for the values it deliberately does not classify — an unrecognised country
   * has no country group — and `NULL = NULL` is false, so `=` drops the whole
   * row on the other side and every ratio at that coordinate reads NULL. That
   * looks exactly like the em dash a legitimately missing denominator produces,
   * which is why it has to be got right rather than noticed.
   */
  sources?: Readonly<Record<string, string>>;
  /**
   * The column the caller's tenant is matched against. Defaults to `tenant_id`.
   *
   * Named rather than assumed because it is the predicate that makes a query
   * safe, and everything else the compiler emits is read from the model. A
   * subject area whose facts spell it differently would otherwise compile
   * cleanly and fail at execution — or, worse, match a column that exists and
   * means something else.
   */
  tenantColumn?: string;
  /** The column {@link ReportSpec.from}/`to` bound. Defaults to `fact_date`. */
  dateColumn?: string;
  /**
   * A column of {@link ReportModelSource.sql} that is unique per fact row.
   *
   * Used as the last term of a LISTING's `ORDER BY`, which is what makes its
   * pages disjoint: `LIMIT`/`OFFSET` over an order with ties may break those
   * ties differently per execution, so a row appears on two pages and another
   * on none. Named rather than assumed for the same reason `tenantColumn` is —
   * a subject area whose facts spell it differently would compile cleanly and
   * page wrongly, which is the kind of bug nobody reports because it looks
   * like a data problem.
   *
   * Optional: without it the compiler falls back to ordering by every selected
   * column, which is not unique but is indistinguishable from unique to a
   * reader — two rows agreeing on everything on screen are interchangeable.
   */
  rowIdColumn?: string;
  /**
   * A relation that can NAME this model's members when its facts cannot.
   *
   * Read for one thing only: resolving the exact KEYS a filter already holds
   * into labels. Never listed from, never aggregated, never a report's rows.
   *
   * The reason one exists: a model whose facts are manufactured FROM the
   * period. A key lookup drops the period predicate so that a saved filter
   * value with no rows on screen still gets its name, and that works while the
   * facts are recorded rows the period merely narrows. Where the SELECT builds
   * its rows out of `:from`/`:to` itself — Missing Timesheets, a row per
   * unfiled day — there is nothing outside the window to find, and a filter on
   * somebody who filed every day rendered as a bare uuid.
   *
   * It must expose the model's tenant and date columns and apply the SAME
   * security clause as the facts: it answers "what is this key called" for a
   * caller who already holds the key, and must not be able to name anything
   * the report would not show them. A dimension whose columns it does not
   * have is looked up in the facts as before.
   */
  lookupSql?: string;
  dimensions: readonly DimensionSource[];
  measures: readonly MeasureSource[];
  joins?: readonly JoinDef[];
}

/** Lookup helpers. Return `undefined` rather than throwing — callers decide. */
export function findDimension<T extends Dimension>(
  model: { dimensions: readonly T[] },
  id: string,
): T | undefined {
  return model.dimensions.find((d) => d.id === id);
}

export function findMeasure<T extends Measure>(
  model: { measures: readonly T[] },
  id: string,
): T | undefined {
  return model.measures.find((m) => m.id === id);
}
